import { useState, type ReactNode } from "react"

import { useLanguage } from "@/clientWeb/i18n/LanguageContext"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { MarkPastOccupiedDialog } from "../components/MarkPastOccupiedDialog"
import { MEETING_ERROR_CODES } from "../constants/meetings.constants"
import { ApiError, meetingsApi } from "../services/meetings.api"
import { useMeetingsStore } from "../store/meetings.store"

/**
 * Mark as Past for a meeting's details screen.
 *
 * The server refuses it while anyone is still in the room. That refusal opens
 * a prompt offering to end the meeting for everyone and mark it past in one
 * go, instead of the generic "could not mark" toast.
 *
 * Shared by the details page and the dashboard's details panel, so both ask
 * the same question and say the same thing afterwards.
 */
export function useMarkPast(
  meetingId: string | undefined,
  opts: { onMarked?: () => void } = {},
): { markPast: () => void; markingPast: boolean; dialog: ReactNode } {
  const { t } = useLanguage()
  const updateMeeting = useMeetingsStore((s) => s.updateMeeting)
  const [markingPast, setMarkingPast] = useState(false)
  const [occupied, setOccupied] = useState(false)

  const run = async (endSession: boolean) => {
    if (!meetingId) return
    setMarkingPast(true)
    try {
      const { meeting: updated } = await meetingsApi.markPast(meetingId, { endSession })
      // Only the end time: the store recomputes status from the times on every
      // write, so a status passed here would be overwritten. The server picks
      // the timestamp and we mirror it back, so the two cannot drift.
      updateMeeting(meetingId, { endTimeMs: updated?.endTimeMs ?? Date.now() })
      setOccupied(false)
      toast.success({ title: t("meetings.markedAsPast") })
      opts.onMarked?.()
    } catch (err) {
      if (err instanceof ApiError && err.code === MEETING_ERROR_CODES.MEETING_OCCUPIED) {
        setOccupied(true)
        return
      }
      setOccupied(false)
      toast.error({ title: t("meetings.markAsPastFailed") })
    } finally {
      setMarkingPast(false)
    }
  }

  const dialog = occupied ? (
    <MarkPastOccupiedDialog
      open
      onOpenChange={(open) => !open && !markingPast && setOccupied(false)}
      onConfirm={() => void run(true)}
      busy={markingPast}
    />
  ) : null

  return { markPast: () => void run(false), markingPast, dialog }
}
